'use client';

import Link from 'next/link';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

interface ServiceUnavailableProps {
  cveId: string;
}

/**
 * Component shown when CVE data cannot be fetched temporarily.
 * The generateMetadata function explicitly sets robots: { index: true, follow: true }
 * for all CVE pages (including error states) since these pages are in the sitemap
 * and API failures are transient. This prevents search engines from deindexing
 * valid CVE pages during NVD API outages.
 */
export default function ServiceUnavailable({ cveId }: ServiceUnavailableProps) {
  return (
    <div className="w-full max-w-3xl mx-auto py-12 px-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-full">
            <AlertTriangle className="w-12 h-12 text-amber-600 dark:text-amber-400" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Service Temporarily Unavailable
        </h1>

        {/* CVE ID */}
        <p className="text-lg font-mono text-gray-700 dark:text-gray-300 mb-4">
          {cveId.toUpperCase()}
        </p>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
          We&apos;re unable to fetch vulnerability data from the NIST National Vulnerability Database at this time.
          This is usually a temporary issue with the upstream API.
        </p>

        {/* Suggestions */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            What you can do:
          </h2>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside">
            <li>Wait a few moments and refresh the page</li>
            <li>Check the NVD status at <a href="https://nvd.nist.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">nvd.nist.gov</a></li>
            <li>Try searching for a different CVE</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href="/tools/security/cve-lookup"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to CVE Lookup
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}
